import React, { useState, useMemo } from 'react';
import { Modal, Form, DatePicker, Select, Button, Space, Alert, Typography, Divider } from 'antd';
import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const PRESETS = [
  {
    label: 'This Month',
    value: [dayjs().startOf('month'), dayjs().endOf('month')],
  },
  {
    label: 'Last Month',
    value: [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    label: 'Last 3 Months',
    value: [dayjs().subtract(3, 'month').startOf('month'), dayjs().endOf('month')],
  },
  {
    label: 'Last 6 Months',
    value: [dayjs().subtract(6, 'month').startOf('month'), dayjs().endOf('month')],
  },
];

const resolveId = (ref) => (ref?._id ? ref._id : ref) ?? '';

const buildSheets = (timesheets, projects, selectedEmployeesMap) => {
  // Project allocation lookup: projectId -> { employeeId -> allocation% }
  const allocMap = {};
  projects.forEach((p) => {
    allocMap[p._id] = {};
    (p.teamMembers || []).forEach((m) => {
      const mid = resolveId(m.employeeId);
      if (mid) allocMap[p._id][mid] = m.allocation ?? 0;
    });
  });

  // --- Sheet 1: Daily Entries ---
  const detailRows = [...timesheets]
    .sort((a, b) => {
      const na = a.employeeName || '';
      const nb = b.employeeName || '';
      if (na !== nb) return na.localeCompare(nb);
      return new Date(a.date) - new Date(b.date);
    })
    .map((ts) => {
      const empId = resolveId(ts.employeeId);
      const projId = resolveId(ts.projectId);
      const emp = selectedEmployeesMap.get(empId);
      const allocation = allocMap[projId]?.[empId];

      return {
        'Employee Name': ts.employeeName || emp?.name || '',
        Department: emp?.department || '',
        Date: dayjs(ts.date).format('DD MMM YYYY'),
        'Project Name': ts.projectName || ts.projectId?.name || '',
        'Allocated %': allocation != null ? `${allocation}%` : '',
        'Task Title': ts.taskTitle || '',
        'Hours Logged': ts.hours ?? 0,
        Description: ts.description || '',
        Status: ts.status
          ? ts.status.charAt(0).toUpperCase() + ts.status.slice(1)
          : '',
      };
    });

  // --- Sheet 2: Employee Summary ---
  const summaryMap = {};

  // Seed with zero rows for every selected employee so they appear even if no entries
  selectedEmployeesMap.forEach((emp, empId) => {
    summaryMap[empId] = {
      name: emp.name,
      department: emp.department || '',
      position: emp.position || '',
      projectIds: new Set(),
      totalHours: 0,
      approvedHours: 0,
      pendingHours: 0,
      days: new Set(),
    };
  });

  timesheets.forEach((ts) => {
    const empId = resolveId(ts.employeeId);
    const projId = resolveId(ts.projectId);
    const entry = summaryMap[empId];
    if (!entry) return;
    if (projId) entry.projectIds.add(projId);
    const hrs = ts.hours ?? 0;
    entry.totalHours += hrs;
    if (ts.status === 'approved') entry.approvedHours += hrs;
    if (ts.status === 'submitted' || ts.status === 'pending') entry.pendingHours += hrs;
    entry.days.add(dayjs(ts.date).format('YYYY-MM-DD'));
  });

  const summaryRows = Object.values(summaryMap)
    .sort((a, b) => b.totalHours - a.totalHours)
    .map((entry) => {
      const avgDaily =
        entry.days.size > 0
          ? Math.round((entry.totalHours / entry.days.size) * 10) / 10
          : 0;
      const approvalRate =
        entry.totalHours > 0
          ? Math.round((entry.approvedHours / entry.totalHours) * 1000) / 10
          : 0;
      return {
        'Employee Name': entry.name,
        Department: entry.department,
        Position: entry.position,
        Projects: entry.projectIds.size,
        'Total Hours': entry.totalHours,
        'Approved Hours': entry.approvedHours,
        'Pending Hours': entry.pendingHours,
        'Avg Daily Hours': avgDaily,
        'Approval Rate %': approvalRate,
      };
    });

  return { detailRows, summaryRows };
};

const makeSheet = (rows, colWidths) => {
  const ws = XLSX.utils.json_to_sheet(rows.length > 0 ? rows : [{}]);
  ws['!cols'] = colWidths.map((wch) => ({ wch }));
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  return ws;
};

const TimesheetExportModal = ({ open, onClose, employees }) => {
  const [form] = Form.useForm();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const activeEmployees = useMemo(
    () => employees.filter((e) => e.isActive),
    [employees]
  );

  const handleAfterOpen = (visible) => {
    if (visible) {
      setError(null);
      form.setFieldsValue({
        dateRange: [dayjs().startOf('month'), dayjs().endOf('month')],
        employeeIds: activeEmployees.map((e) => e._id),
      });
    }
  };

  const handleExport = async (values) => {
    setExporting(true);
    setError(null);
    try {
      const [start, end] = values.dateRange;
      const startStr = start.format('YYYY-MM-DD');
      const endStr = end.format('YYYY-MM-DD');

      const [tsRes, projRes] = await Promise.all([
        fetch(`/api/timesheets?startDate=${startStr}&endDate=${endStr}`),
        fetch('/api/projects'),
      ]);

      const [tsData, projData] = await Promise.all([tsRes.json(), projRes.json()]);

      if (!tsData.success) throw new Error('Failed to fetch timesheets');
      if (!projData.success) throw new Error('Failed to fetch projects');

      const selectedIds = new Set(values.employeeIds);
      const selectedEmployeesMap = new Map(
        employees
          .filter((e) => selectedIds.has(e._id))
          .map((e) => [e._id, e])
      );

      const filteredTimesheets = (tsData.data || []).filter((ts) => {
        const empId = resolveId(ts.employeeId);
        return selectedIds.has(empId);
      });

      const { detailRows, summaryRows } = buildSheets(
        filteredTimesheets,
        projData.data || [],
        selectedEmployeesMap
      );

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        makeSheet(detailRows, [22, 16, 14, 30, 13, 30, 14, 44, 12]),
        'Daily Entries'
      );
      XLSX.utils.book_append_sheet(
        wb,
        makeSheet(summaryRows, [22, 16, 22, 10, 14, 16, 14, 16, 16]),
        'Employee Summary'
      );

      const dateLabel =
        start.format('MMM-YYYY') === end.format('MMM-YYYY')
          ? start.format('MMM-YYYY')
          : `${start.format('MMM-YYYY')}_to_${end.format('MMM-YYYY')}`;
      XLSX.writeFile(wb, `timesheets_${dateLabel}.xlsx`);

      onClose();
    } catch (err) {
      setError(err.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const employeeOptions = useMemo(
    () =>
      [...employees]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((emp) => ({
          label: emp.isActive ? emp.name : `${emp.name} (Inactive)`,
          value: emp._id,
        })),
    [employees]
  );

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: '#22c55e' }} />
          <span>Export Timesheets to Excel</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      afterOpenChange={handleAfterOpen}
      footer={null}
      width={580}
      destroyOnClose
    >
      {error && (
        <Alert
          message={error}
          type="error"
          className="mb-4"
          closable
          onClose={() => setError(null)}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleExport}>
        <Form.Item
          name="dateRange"
          label="Date Range"
          rules={[{ required: true, message: 'Please select a date range' }]}
        >
          <RangePicker
            className="w-full"
            presets={PRESETS}
            format="DD MMM YYYY"
            allowClear={false}
          />
        </Form.Item>

        <Form.Item
          name="employeeIds"
          label={
            <Space size={4}>
              <span>Employees</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                (active employees selected by default)
              </Text>
            </Space>
          }
          rules={[
            { required: true, message: 'Please select at least one employee' },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Search and select employees"
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            options={employeeOptions}
            dropdownRender={(menu) => (
              <>
                <div className="px-3 py-2 flex gap-3 border-b border-gray-100">
                  <Button
                    size="small"
                    type="link"
                    className="p-0 h-auto"
                    onClick={() =>
                      form.setFieldValue(
                        'employeeIds',
                        activeEmployees.map((e) => e._id)
                      )
                    }
                  >
                    Active Only
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    className="p-0 h-auto"
                    onClick={() =>
                      form.setFieldValue(
                        'employeeIds',
                        employees.map((e) => e._id)
                      )
                    }
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    danger
                    className="p-0 h-auto"
                    onClick={() => form.setFieldValue('employeeIds', [])}
                  >
                    Clear
                  </Button>
                </div>
                {menu}
              </>
            )}
          />
        </Form.Item>

        <Divider className="my-4" />

        <div className="bg-gray-50 rounded p-3 mb-4 text-sm text-gray-600">
          <Text type="secondary">
            The export will generate a <strong>.xlsx</strong> file with two sheets:
          </Text>
          <ul className="mt-1 mb-0 pl-4 space-y-1">
            <li>
              <strong>Daily Entries</strong> — per-row timesheet detail with project, task,
              hours, description and status
            </li>
            <li>
              <strong>Employee Summary</strong> — total hours, approved hours, avg daily
              hours and approval rate per employee
            </li>
          </ul>
        </div>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={exporting}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={exporting}
            icon={<DownloadOutlined />}
            style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }}
          >
            {exporting ? 'Generating…' : 'Export Excel'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default TimesheetExportModal;
